import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import SimpleVerificationReport from '../components/verifiers/SimpleVerificationReport';
import simpleVerificationService from '../services/simpleVerificationService';

/**
 * Simple Verification Report Page
 * Display AI-generated report with approval/dispute options
 */
const SimpleVerificationReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVerification();
  }, [id]);

  const loadVerification = async () => {
    try {
      setLoading(true);
      const response = await simpleVerificationService.getVerification(id);
      
      if (response.success) {
        setVerification(response.data);
      } else {
        setError('Failed to load verification report');
      }
    } catch (err) {
      console.error('Error loading verification:', err);
      setError('Failed to load verification report');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async ({ approvalNotes, rating }) => {
    try {
      const response = await simpleVerificationService.approveVerification(id, approvalNotes);
      
      if (response.success) {
        // Reload to show updated status
        await loadVerification();
        alert('Verification approved and payment released!');
      }
    } catch (err) {
      console.error('Error approving verification:', err);
      alert('Failed to approve verification');
    }
  };

  const handleDispute = async ({ disputeReason }) => {
    try {
      const response = await simpleVerificationService.disputeVerification(id, disputeReason);
      
      if (response.success) {
        // Reload to show updated status
        await loadVerification();
        alert('Dispute submitted. Our team will review and contact you.');
      }
    } catch (err) {
      console.error('Error disputing verification:', err);
      alert('Failed to submit dispute');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!verification) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Verification report not found</Alert>
      </Box>
    );
  }

  // Check if user is client
  const isClient = user && user.id === verification.clientId;

  return (
    <SimpleVerificationReport
      verification={verification}
      isClient={isClient}
      onApprove={handleApprove}
      onDispute={handleDispute}
    />
  );
};

export default SimpleVerificationReportPage;
